import { useState, useEffect, useMemo } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import InputDropdown from "../../../components/inputs/InputDropdown/InputDropdown";
import BarChartGraph from "../../../components/common/Charts/BarChartGraph";
import Table from "../../../components/common/Table/Table";
import { getApiInstance } from "../../../utils/api";
import TabsNavigation from "../../../components/common/TabsNavigation/TabsNavigation";
import { formatCurrency } from "../../../utils/utils";
import "../cantidadesGeneradas/CantidadesGeneradas.scss";

export default function SegregacionValorizacion() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [informeData, setInformeData] = useState([]);
    const [activeTab, setActiveTab] = useState("grafica");

    const [tipoDesglose, setTipoDesglose] = useState("cadena");

    const opcionesDesglose = [
        { value: "cadena", label: "Desglose por Cadena" },
        { value: "tipoObra", label: "Desglose por Tipo de Obra" },
        { value: "pais", label: "Desglose por País" },
    ];

    useEffect(() => {
        const fetchInformeSegregacion = async () => {
            setLoading(true);
            setError(null);
            try {
                const api = getApiInstance();
                const response = await api.get(`/api/Informe/GetSegregacion`);
                setInformeData(response.data);
            } catch (err) {
                setError(
                    "No se ha podido cargar el informe. Vuelve a intentarlo más tarde."
                );
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchInformeSegregacion();
    }, []);

    // --- LÓGICA CENTRAL REFACTORIZADA CON useMemo ---
    // La lógica ahora es mucho más simple: solo agrupa los datos ya procesados.
    const { datosGrafico, datosTabla, categoriaLabel } = useMemo(() => {
        if (informeData.length === 0) {
            return {
                datosGrafico: [],
                datosTabla: [],
                categoriaLabel: "Categoría",
            };
        }

        // 1. Define la clave para agrupar según el desglose seleccionado
        const config = {
            cadena: { key: "cadena", label: "Cadena" },
            tipoObra: { key: "tipoObra", label: "Tipo de Obra" },
            pais: { key: "pais", label: "País" },
        }[tipoDesglose];

        // 2. Agrupa los resultados y calcula los totales y promedios ponderados
        const agrupado = informeData.reduce((acc, item) => {
            const groupName = item[config.key];
            if (!acc[groupName]) {
                acc[groupName] = {
                    totalObras: 0,
                    // Acumuladores para el promedio ponderado
                    weightedValorizacion: 0,
                    weightedSegregacion: 0,
                };
            }

            acc[groupName].totalObras += item.numObras;
            // Ponderamos el porcentaje por el número de obras
            acc[groupName].weightedValorizacion +=
                item.porcentajeValorizacion * item.numObras;
            acc[groupName].weightedSegregacion +=
                item.porcentajeSegregacion * item.numObras;

            return acc;
        }, {});

        // 3. Convierte el objeto agrupado en un array final con los promedios calculados
        const tablaProcesada = Object.keys(agrupado).map((groupName) => {
            const group = agrupado[groupName];
            const porcentajeValorizacion =
                group.totalObras > 0
                    ? group.weightedValorizacion / group.totalObras
                    : 0;
            const porcentajeSegregacion =
                group.totalObras > 0
                    ? group.weightedSegregacion / group.totalObras
                    : 0;

            return {
                categoria: groupName,
                numObras: group.totalObras,
                porcentajeValorizacion,
                porcentajeSegregacion,
            };
        });

        // 4. Formatea los datos para el gráfico
        const graficoProcesado = tablaProcesada.map((item) => ({
            name: item.categoria,
            // Las claves deben coincidir con las series del gráfico
            Valorización: parseFloat(item.porcentajeValorizacion.toFixed(2)),
            Segregación: parseFloat(item.porcentajeSegregacion.toFixed(2)),
        }));

        return {
            datosGrafico: graficoProcesado,
            datosTabla: tablaProcesada,
            categoriaLabel: config.label,
        };
    }, [informeData, tipoDesglose]);

    // Las columnas de la tabla usan la etiqueta dinámica
    const columnasTabla = [
        {
            key: "categoria",
            label: categoriaLabel,
            visible: true,
            sortable: true,
        },
        {
            key: "numObras",
            label: "Nº Obras",
            visible: true,
            sortable: true,
            type: "number",
        },
        {
            key: "porcentajeValorizacion",
            label: "% Valorización",
            visible: true,
            sortable: true,
            type: "number",
            render: (row) => `${row.porcentajeValorizacion.toFixed(2)} %`,
        },
        {
            key: "porcentajeSegregacion",
            label: "% Segregación",
            visible: true,
            sortable: true,
            type: "number",
            render: (row) => `${row.porcentajeSegregacion.toFixed(2)} %`,
        },
    ];

    const tabs = [
        { id: "grafica", label: "Gráfica" },
        { id: "listado", label: "Listado" },
    ];

    // Renderizado del contenido del gráfico
    const renderGraficaContent = () => (
        <div className="cantidades-generadas-content">
            <div className="graficas-card">
                <div className="charts-section">
                    <div className="chart-container bar-chart-container">
                        <div className="chart-header">
                            <h3>Costes</h3>
                            {/* <span className="total-amount">
                <strong>Real:</strong> {formatCurrency(totalCosteReal)}
              </span>
              <span className="total-amount">
                <strong>Previsto:</strong> {formatCurrency(totalCostePrevisto)}
              </span> */}
                        </div>
                        <div className="chart-wrapper">
                            <BarChartGraph
                                data={{ all: datosGrafico }}
                                activeTab="all"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Renderizado del contenido de la tabla
    const renderListadoContent = () => (
        <div className="listado-content">
            <Table
                columns={columnasTabla}
                dataTable={datosTabla}
                keyProp="categoria"
                sortProp="categoria"
                noResultsMessage="No hay datos para el desglose seleccionado."
                elementName={categoriaLabel.toLowerCase()}
            />
        </div>
    );

    return (
        <PageLayout
            title="Informe de Segregación y Valorización"
            subtitle="Análisis de porcentajes por diferentes criterios de desglose"
        >
            <div className="filters-section-common">
                <InputDropdown
                    options={opcionesDesglose}
                    value={opcionesDesglose.find(
                        (opt) => opt.value === tipoDesglose
                    )}
                    onChange={(option) => setTipoDesglose(option.value)}
                    placeholder="Seleccionar desglose"
                    labelVisible={false}
                />
            </div>

            <TabsNavigation
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
            />

            {error && <div className="error-message">{error}</div>}

            {loading ? (
                <div className="loading-message">Cargando datos...</div>
            ) : activeTab === "grafica" ? (
                renderGraficaContent()
            ) : (
                renderListadoContent()
            )}
        </PageLayout>
    );
}
